<template>
  <v-card style="height: calc(100vh - 48px)">
    <v-card-text>
      <v-row class="mx-0">
        <v-col cols="2">
          <select-project
            v-model="project"
          />
          <v-select
            v-model="searchBy"
            class="mt-2"
            label="Search by"
            :items="['login', 'account_id']"
            hide-details
          />
          <v-text-field
            v-model="accountValue"
            class="mt-2"
            :label="searchBy === 'login' ? 'Login' : 'Account ID'"
            :hint="`Enter ${searchBy}`"
            hide-details="auto"
            clearable
          />
          <v-select
            v-model="withLogin"
            class="mt-2"
            label="Login Mode"
            :items="withLoginItems"
            item-text="text"
            item-value="value"
            hide-details
          />
          <v-select
            v-model="mode"
            class="mt-2"
            label="Game Mode"
            :items="modeItems"
            item-text="text"
            item-value="value"
            hide-details
          />
          <v-select
            v-model="platform"
            class="mt-2"
            label="Platform"
            :items="platformItems"
            item-text="text"
            item-value="value"
            hide-details
          />
          <v-autocomplete
            v-model="selectedProvider"
            :items="providerSystemsAliases"
            :loading="loadingGames"
            class="mt-2"
            clearable
            hide-details
            label="Provider"
            :disabled="!project"
          />
          <v-autocomplete
            v-model="selectedBrand"
            :items="providerBrandsAliases"
            :loading="loadingGames"
            class="mt-2"
            clearable
            hide-details
            label="Brand"
            :disabled="!project"
          />
          <v-autocomplete
            v-model="selectedGame"
            :items="filteredGames"
            :loading="loadingGames"
            class="mt-2"
            clearable
            hide-details
            item-text="name"
            item-value="game_id"
            label="Game"
            :disabled="!project"
          >
            <template slot="item" slot-scope="{ item }">
              <div style="width: 100%">
                <span v-html="item.name"></span>
                <small class="float-right">({{ item.system_name }})</small>
              </div>
            </template>
          </v-autocomplete>
          <div class="mt-2">
            <span class="caption">Proxy country:</span>
            <select-countries
              :chosenCountries="proxyCountry"
              :countries="countries"
              :multiple="false"
              :disabled="!Boolean(countries.length)"
              :show-clear-action="true"
              :loading="loadingProxyCountries"
              @input="(items) => proxyCountry = items.value"
              @clear="onClearProxyCountry"
              class="select-countries"
            ></select-countries>
          </div>
        </v-col>
        <v-col cols="10">
          <div class="pa-4">
            <div class="text-center text-h6 mb-4">
              Game Launch Configuration
            </div>

            <vue-json-editor
              v-model="editableGameConfig"
              mode="code"
              :modes="[]"
              :show-btns="false"
              :expandedOnStart="true"
              class="mb-4"
            />

            <div class="text-center">
              <v-btn
                color="primary"
                :disabled="!allFieldsFilled"
                :loading="isLaunchLoading"
                @click="onLaunch"
              >
                Confirm
              </v-btn>
            </div>

            <div v-if="gameUrl" class="mt-4">
              <v-text-field
                v-model="gameUrl"
                label="Game URL"
                readonly
                outlined
                dense
              >
                <template v-slot:append>
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="openUrl"
                    class="mr-1"
                  >
                    <v-icon small>open_in_new</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="grey"
                    @click="copyUrl"
                  >
                    <v-icon small>content_copy</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
  import vueJsonEditor from 'vue-json-editor'
  import { API } from '@/utils/api'
  import SelectCountries from '@/components/core/SelectCountries.vue'

  export default {
    components: {
      SelectCountries,
      SelectProject,
      vueJsonEditor,
    },
    props: {
      games: {
        type: Array,
        default: () => []
      },
      countries: {
        type: Array,
        default: () => []
      },
      loadingGames: {
        type: Boolean,
        default: false
      },
      loadingProxyCountries: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        project: null,
        searchBy: 'login',
        accountValue: '',
        withLogin: true,
        mode: 'real',
        platform: 'desktop',
        selectedProvider: null,
        selectedBrand: null,
        selectedGame: null,
        proxyCountry: null,
        editableGameConfig: {},
        gameUrl: '',
        isLaunchLoading: false,
        withLoginItems: [
          { text: 'With Login', value: true },
          { text: 'Without Login', value: false }
        ],
        modeItems: [
          { text: 'Demo', value: 'demo' },
          { text: 'Real', value: 'real' }
        ],
        platformItems: [
          { text: 'Desktop', value: 'desktop' },
          { text: 'Mobile', value: 'mobile' },
          { text: 'API', value: 'api' }
        ]
      }
    },

    computed: {
      providerSystemsAliases () {
        return Array.from(
          new Map(
            this.games
              .filter(game => game.system_name && game.system_id)
              .map(game => [ game.system_id, { text: game.system_name, value: game.system_id } ])
          ).values()
        )
      },

      providerBrandsAliases () {
        let games = this.games
        if (this.selectedProvider) {
          games = games.filter(game => game.system_id === this.selectedProvider)
        }

        return Array.from(
          new Map(
            games
              .filter(game => game.brand_alias && game.brand_id)
              .map(game => [ game.brand_id, { text: game.brand_alias, value: game.brand_id } ])
          ).values()
        )
      },

      filteredGames () {
        let filtered = [ ...this.games ]

        if (this.selectedProvider) {
          filtered = filtered.filter(game => game.system_id === this.selectedProvider)
        }

        if (this.selectedBrand) {
          filtered = filtered.filter(game => game.brand_id === this.selectedBrand)
        }

        return filtered
      },

      allFieldsFilled () {
        return this.project &&
          this.accountValue &&
          this.selectedGame &&
          this.selectedProvider &&
          this.selectedBrand &&
          this.mode &&
          this.platform
      },

      gameConfig () {
        const selectedGameData = this.games.find(game => game.game_id === this.selectedGame)

        return {
          mode: this.mode,
          platform: this.platform,
          name: selectedGameData?.name || '',
          system: (selectedGameData?.system_name || ''),
          game_id: this.selectedGame || '',
          brand_id: this.selectedBrand || '',
          [this.searchBy]: this.accountValue || '',
          with_login: this.withLogin,
          proxyCountry: this.proxyCountry || '',
        }
      }
    },

    methods: {
      resetGameSelections () {
        this.selectedProvider = null
        this.selectedBrand = null
        this.selectedGame = null
      },
      onClearProxyCountry () {
        this.proxyCountry = null
      },
      onLaunch () {
        const launchData = {
          ...this.editableGameConfig,
          account: this.accountValue,
          with_login: this.withLogin,
          entity_id: this.project,
          search_by: this.searchBy
        }

        this.isLaunchLoading = true
        return API.call('/$v/tools/game_launcher.json', { launchData })
          .then(resp => {
            this.gameUrl = resp.data || ''
          })
          .catch(e => {
            console.error('Error loading games:', e)
          })
          .finally(() => {
            this.isLaunchLoading = false
          })
      },

      async copyUrl () {
        await navigator.clipboard.writeText(this.gameUrl).then(() => {
          this.$showSuccess('URL copied to clipboard')
        }).catch(err => {
          console.error('Error copying URL:', err)
          this.$showError('Error copying URL')
        })
      },

      openUrl () {
        if (this.gameUrl) {
          window.open(this.gameUrl, '_blank')
        }
      },

      emitLoadProxyCountries () {
        this.$emit('project-provider-selected', {
          entity_id: this.project,
          system_id: this.selectedProvider,
          brand_id: this.selectedBrand
        })
      }
    },

    watch: {
      project: function () {
        if (this.project) {
          this.$emit('project-selected', this.project)
        } else {
          this.resetGameSelections()
        }

        this.emitLoadProxyCountries()
      },

      searchBy () {
        this.accountValue = ''
      },

      selectedProvider () {
        if (this.selectedGame && this.selectedProvider) {
          const selectedGameData = this.games.find(game => game.game_id === this.selectedGame)
          if (selectedGameData && selectedGameData.system_id !== this.selectedProvider) {
            this.selectedGame = null
          }
        }

        this.emitLoadProxyCountries()
      },

      selectedBrand () {
        if (this.selectedGame && this.selectedBrand) {
          const selectedGameData = this.games.find(game => game.game_id === this.selectedGame)
          if (selectedGameData && selectedGameData.brand_id !== this.selectedBrand) {
            this.selectedGame = null
          }
        }

        this.emitLoadProxyCountries()
      },

      selectedGame () {
        if (this.selectedGame) {
          const selectedGameData = this.games.find(game => game.game_id === this.selectedGame)
          if (selectedGameData) {
            this.selectedProvider = selectedGameData.system_id
            this.selectedBrand = selectedGameData.brand_id
          }
        }
      },

      gameConfig: {
        handler (newConfig) {
          this.editableGameConfig = { ...newConfig }
        },
        deep: true,
        immediate: true
      }
    }
  }
</script>
<style lang="scss" scoped>
  ::v-deep .jsoneditor-menu {
    display: none !important;
  }

  .select-countries {
    ::v-deep .custom-select {
      max-width: none;
    }

    ::v-deep .v-input__slot {
      padding: 0 0 0 12px !important;
    }
  }
</style>

